import { useEffect, useRef, useState } from 'react';
import { modalTokens } from './tokens';

interface UseModalBehaviorOptions {
  isOpen: boolean;
  onClose: () => void;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  lockScroll?: boolean;
  trapFocus?: boolean;
  exitDurationMs?: number;
}

interface UseModalBehaviorResult {
  shouldRender: boolean;
  isEntering: boolean;
  isExiting: boolean;
  contentRef: React.RefObject<HTMLDivElement | null>;
  handleOverlayClick: (e: React.MouseEvent) => void;
}

let scrollLockCount = 0;
let previousBodyOverflow = '';

function acquireScrollLock() {
  if (scrollLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount += 1;
}

function releaseScrollLock() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = previousBodyOverflow;
  }
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useModalBehavior({
  isOpen,
  onClose,
  closeOnEsc = true,
  closeOnOverlayClick = true,
  lockScroll = true,
  trapFocus = true,
  exitDurationMs = modalTokens.transitionDurationMs,
}: UseModalBehaviorOptions): UseModalBehaviorResult {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isEntering, setIsEntering] = useState(isOpen);
  const [isExiting, setIsExiting] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsEntering(true);
      setIsExiting(false);
      return;
    }

    if (!shouldRender) return;

    setIsExiting(true);
    const timeout = setTimeout(() => {
      setShouldRender(false);
      setIsExiting(false);
    }, exitDurationMs);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isEntering) return;
    const frame = requestAnimationFrame(() => setIsEntering(false));
    return () => cancelAnimationFrame(frame);
  }, [isEntering]);

  useEffect(() => {
    if (!shouldRender || !lockScroll) return;
    acquireScrollLock();
    return () => releaseScrollLock();
  }, [shouldRender, lockScroll]);

  useEffect(() => {
    if (!shouldRender) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldRender) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (closeOnEsc && e.key === 'Escape') {
        onClose();
        return;
      }

      if (trapFocus && e.key === 'Tab' && contentRef.current) {
        const focusable = Array.from(contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
          el => !el.hasAttribute('disabled'),
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, closeOnEsc, trapFocus]);

  function handleOverlayClick(e: React.MouseEvent) {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  }

  return { shouldRender, isEntering, isExiting, contentRef, handleOverlayClick };
}
